/**
 * Step 5 — integrate players (owner: physics team): gravity (none during the fake-jump freeze), fall limits
 * (fast-fall, slam), position, ground contact (LAND + the landing state: LANDING lag, leap-whiff recovery, slam
 * STUMBLE), the soft PLAY_MIN_X / PLAY_MAX_X clamp, head-vs-head push-apart (non-solid while a pass / toss is in
 * the air, while a spin's i-frames run, during lunges, for downed / stunned heads and for a defender beaten by a
 * counter-square win; Mirage decoys are never solid), the glove spring, the pre-snap offense clamp behind the LOS
 * (head AND the ball in its glove) and the hop-run phase. Semi-implicit Euler at the fixed DT.
 */
import { DT, FRAMES, GLOVE, KICK, MOTION, MOVE, PHYSICS, PLAY_MAX_X, PLAY_MIN_X } from '@hf/shared';
import { commitFailed } from '../contact/fx';
import { BallState, CommitKind, Phase, PlayerState } from '../enums';
import { lungeWhiffed } from '../player/controller';
import { spinInvulnerable } from '../player/frames';
import { PF, keepsStateOnLanding, setLock, setState } from '../player/fsm';
import { kickoffPending } from '../selectors';
import type { SimEventSink } from '../sink';
import type { PlayerSim, SimState } from '../state';

/** |normal.y| above which an airborne head is "on top" of the other head (slides off sideways). */
const HEAD_TOP_NORMAL = 0.7;
/** Hop cadence limits, × the base rate (a slow shuffle still hops; a dash hops at most twice as fast). */
const HOP_MIN_RATE = 0.5;
const HOP_MAX_RATE = 2;

const land = (s: SimState, ev: SimEventSink, p: PlayerSim): void => {
  const impact = -p.vy;
  p.y = p.stats.headRadius;
  p.vy = 0;
  p.grounded = true;
  p.airFrame = 0;
  p.hurdling = false;
  p.flags &= ~(PF.FAST_FALL | PF.FAST_FALL_USED);
  if (ev) ev.push({ type: 'LAND', tick: s.tick, actor: p.index, x: p.x, y: 0, impact });
  switch (p.state) {
    case PlayerState.FAKE_FREEZE:
    case PlayerState.FAKE_SLAM:
      setLock(p, PlayerState.STUMBLE, FRAMES.fakeCancel.stumble);
      if (ev) ev.push({ type: 'STUMBLE', tick: s.tick, actor: p.index, x: p.x, y: p.y });
      break;
    case PlayerState.LEAP_GRAB:
      lungeWhiffed(s, ev, p, 'leap');
      setLock(
        p,
        PlayerState.LANDING,
        (p.flags & PF.LUNGE_WHIFF) !== 0 ? FRAMES.leapGrab.recovery : MOVE.landingLagFrames,
      );
      break;
    case PlayerState.AIRBORNE:
      if (MOVE.landingLagFrames > 0) setLock(p, PlayerState.LANDING, MOVE.landingLagFrames);
      else setState(p, PlayerState.IDLE);
      break;
    default:
      if (!keepsStateOnLanding(p.state)) setState(p, PlayerState.IDLE);
  }
  // A plain jump that came down without tackling anyone (a lunge records its own result).
  if (p.commitKind === CommitKind.JUMP) commitFailed(s, ev, p);
};

const movePlayer = (s: SimState, ev: SimEventSink, p: PlayerSim): void => {
  const r = p.stats.headRadius;
  if (!p.grounded && p.state !== PlayerState.FAKE_FREEZE) {
    p.vy -= PHYSICS.gravity * DT;
    if (p.state !== PlayerState.FAKE_SLAM) {
      const lim = (p.flags & PF.FAST_FALL) !== 0 ? PHYSICS.fastFallSpeed : PHYSICS.maxFallSpeed;
      if (p.vy < -lim) p.vy = -lim;
    }
  }
  p.x += p.vx * DT;
  p.y += p.vy * DT;
  if (p.grounded) {
    if (p.vy > 0) {
      p.grounded = false;
      if (p.airFrame === 0) p.airFrame = 1;
    } else {
      p.y = r;
      p.vy = 0;
    }
  } else if (p.y <= r && p.vy <= 0) land(s, ev, p);
  if (p.x < PLAY_MIN_X) {
    p.x = PLAY_MIN_X;
    if (p.vx < 0) p.vx = 0;
  } else if (p.x > PLAY_MAX_X) {
    p.x = PLAY_MAX_X;
    if (p.vx > 0) p.vx = 0;
  }
};

/** States whose head is not a solid body for push-apart. */
const ghost = (p: PlayerSim): boolean =>
  p.beatenFrames > 0 ||
  spinInvulnerable(p) ||
  p.state === PlayerState.DIVE ||
  p.state === PlayerState.LEAP_GRAB ||
  p.state === PlayerState.TACKLED ||
  p.state === PlayerState.STUNNED;

/**
 * Head-vs-head separation: resolve PHYSICS.bodyPushResolve of the overlap per tick, split evenly, and cancel the
 * approaching relative velocity along the normal (inelastic). Grounded heads stay on the ground; an airborne head
 * resting on top of the other slides off sideways (no head-stacking stalemates).
 */
const pushApart = (s: SimState): void => {
  // A pass / toss in flight or a loose kick: nobody carries the ball, so there is nothing to block or tackle —
  // heads pass through each other (a QB chasing his pass must not shove a standing defender onto the catch point).
  // Pre-snap too: a QB standing on the line must not wall off a flinching defender (he would never be OFFSIDE).
  const bs = s.ball.state;
  if (bs === BallState.AIR || bs === BallState.LOOSE || s.rules.phase === Phase.PRE_SNAP) return;
  const a = s.players[0];
  const b = s.players[1];
  if (ghost(a) || ghost(b)) return;
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const rs = a.stats.headRadius + b.stats.headRadius;
  const d2 = dx * dx + dy * dy;
  if (d2 >= rs * rs) return;
  // Coincident centres: separate along x deterministically (player 0 to the left). Written without merging
  // doubles with constants (no HeapNumber boxing on this per-contact path).
  const d = Math.sqrt(d2);
  const inv = 1 / Math.max(d, 1e-9);
  const nx = dx * inv + (d < 1e-9 ? 1 : 0);
  const ny = dy * inv;
  const corr = (rs - d) * PHYSICS.bodyPushResolve * 0.5;
  a.x -= nx * corr;
  b.x += nx * corr;
  if (!a.grounded) a.y -= ny * corr;
  if (!b.grounded) b.y += ny * corr;
  // never pushed into the turf (the next tick lands it); Math.max, not `if (y < r) y = r` (that boxes y)
  a.y = Math.max(a.y, a.stats.headRadius);
  b.y = Math.max(b.y, b.stats.headRadius);
  // inelastic: remove the approaching component of the relative velocity
  const vrel = (b.vx - a.vx) * nx + (b.vy - a.vy) * ny;
  if (vrel < 0) {
    const j = -vrel * 0.5;
    a.vx -= nx * j;
    b.vx += nx * j;
    if (!a.grounded) a.vy -= ny * j;
    if (!b.grounded) b.vy += ny * j;
  }
  // head-top slide-off
  const top = ny > 0 ? b : ny < 0 ? a : null;
  if (top && !top.grounded && (ny > 0 ? ny : -ny) > HEAD_TOP_NORMAL) {
    const side = nx > 0 ? 1 : nx < 0 ? -1 : top.index === 0 ? -1 : 1;
    const want = side * (ny > 0 ? 1 : -1) * MOTION.headSlideSpeed;
    if ((want > 0 && top.vx < want) || (want < 0 && top.vx > want)) top.vx = want;
  }
};

const springGlove = (p: PlayerSim): void => {
  let gx = p.gloveX + (p.gloveTargetX - p.gloveX) * GLOVE.follow;
  let gy = p.gloveY + (p.gloveTargetY - p.gloveY) * GLOVE.follow;
  const l2 = gx * gx + gy * gy;
  if (l2 > GLOVE.maxReach * GLOVE.maxReach) {
    const k = GLOVE.maxReach / Math.sqrt(l2);
    gx *= k;
    gy *= k;
  }
  p.gloveX = gx;
  p.gloveY = gy;
};

/** Hop-run cadence (render squash/stretch + footsteps): one hop per hopPeriodFrames at full run speed. */
const hop = (p: PlayerSim): void => {
  const speed = Math.abs(p.vx);
  const running = p.grounded && speed > 0 && (p.state === PlayerState.RUN || p.state === PlayerState.DASH);
  if (!running && p.hopPhase === 0) return;
  // running: the base cadence × speed / run speed, clamped; stopping: finish the hop at the base cadence.
  // (Math.min/max and integer selects only: merging a double with a constant would box it every tick.)
  const k = Math.min(Math.max(speed / MOVE.runSpeed, HOP_MIN_RATE), HOP_MAX_RATE);
  const run = running ? 1 : 0;
  const next = p.hopPhase + (k * run + (1 - run)) / MOVE.hopPeriodFrames;
  // wrap while running; rest at 0 once a stopping hop completes
  const wrap = next >= 1 ? 1 : 0;
  p.hopPhase = next - wrap * (run + (1 - run) * next);
};

/**
 * Pre-snap: neither the offense's head centre nor the ball in its glove may pass the LOS (it cannot be offside,
 * and a goal-line snap must start with the ball behind the line). The ball is placed by the ball module after
 * this step, at head + facing × glove (just sprung), so the clamp predicts it from the glove.
 */
const clampOffensePreSnap = (s: SimState): void => {
  const r = s.rules;
  const o = s.players[r.offense as 0 | 1];
  const b = s.ball;
  const head = (o.x - r.los) * r.attackDir;
  const holds = b.holder === o.index && b.state === BallState.HELD;
  const over = holds ? Math.max(head, head + o.facing * o.gloveX * r.attackDir) : head;
  if (over > 0) {
    o.x -= over * r.attackDir;
    if (o.vx * r.attackDir > 0) o.vx = 0;
  }
};

/**
 * Kickoff not yet kicked: the receiver stays at least KICK.restrainYards from the LOS (the restraining line); he
 * may move freely behind it.
 */
const clampKickoffReceiver = (s: SimState): void => {
  const r = s.rules;
  const p = s.players[(1 - r.offense) as 0 | 1];
  const over = KICK.restrainYards - (p.x - r.los) * r.attackDir;
  if (over <= 0) return;
  p.x += over * r.attackDir;
  if (p.vx * r.attackDir < 0) p.vx = 0;
};

export const integrate = (s: SimState, ev: SimEventSink): void => {
  for (let i = 0; i < 2; i++) movePlayer(s, ev, s.players[i as 0 | 1]);
  pushApart(s);
  springGlove(s.players[0]);
  springGlove(s.players[1]);
  if (kickoffPending(s)) {
    clampOffensePreSnap(s);
    clampKickoffReceiver(s);
  } else if (s.rules.phase === Phase.PRE_SNAP) clampOffensePreSnap(s);
  hop(s.players[0]);
  hop(s.players[1]);
};
